import React, { useEffect, useMemo } from 'react'
import { useGetMeQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { CrossClientQueryDataSource } from '../../../../models/CrossClient'
import {
  CROSS_CLIENT_QUERY_SUPPORTED_INTEGRATIONS,
  getIntegrationDisplayName,
  IntegrationCategory,
  IntegrationServiceId,
} from '../../../../common/integrations'
import { Button } from '../../../../components/Button/Button'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'
import { Form } from 'react-bootstrap'
import { ReactComponent as ZocksIcon } from '../../../../assets/images/ZocksIcon-primary.svg'
import './CrossClientSources.scss'

export interface CrossClientSourcesProps {
  sources: CrossClientQueryDataSource[]
  onChange: (newSources: CrossClientQueryDataSource[]) => void
}

const _MAX_INTEGRATION_SOURCES = 2

export const CrossClientSources: React.FC<CrossClientSourcesProps> = ({ sources, onChange }) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: currentUser, isLoading: userIsLoading } = useGetMeQuery({ accountId })

  const supportedUserIntegrationsByCategory = useMemo(() => {
    if (!currentUser?.integrations) {
      return {} as Partial<Record<IntegrationCategory, IntegrationServiceId[]>>
    }

    const userServiceIds = new Set(currentUser.integrations.map((i) => i.serviceId))

    return Object.entries(CROSS_CLIENT_QUERY_SUPPORTED_INTEGRATIONS).reduce(
      (acc, [category, supportedServiceIds]) => {
        const matchingServiceIds = supportedServiceIds.filter((serviceId) => userServiceIds.has(serviceId))
        if (matchingServiceIds.length > 0) {
          acc[category as IntegrationCategory] = matchingServiceIds
        }
        return acc
      },
      {} as Partial<Record<IntegrationCategory, IntegrationServiceId[]>>,
    )
  }, [currentUser])

  const hasSupportedIntegrations = !!Object.keys(supportedUserIntegrationsByCategory).length

  const defaultSources = useMemo(() => {
    if (!Object.keys(supportedUserIntegrationsByCategory).length) {
      return []
    }

    const defaultSources: CrossClientQueryDataSource[] = []

    const crmIntegrations = supportedUserIntegrationsByCategory[IntegrationCategory.CRM]
    if (crmIntegrations?.length) {
      defaultSources.push({ type: 'integration', integrationType: crmIntegrations[0] })
    }

    const planningIntegrations = supportedUserIntegrationsByCategory[IntegrationCategory.PLANNING]
    if (planningIntegrations?.length) {
      defaultSources.push({ type: 'integration', integrationType: planningIntegrations[0] })
    }
    return defaultSources
  }, [supportedUserIntegrationsByCategory])

  useEffect(() => {
    if (defaultSources.length) {
      onChange(defaultSources)
    }
  }, [defaultSources, onChange])

  if (userIsLoading) {
    return null
  }

  const sourcesBase = (
    <>
      I'll check available data in <ZocksIcon width='0.75rem' height='0.75rem' /> <span className='medium'>Zocks</span>, like client profiles and meetings.
    </>
  )

  if (!hasSupportedIntegrations) {
    return <div className='paragraph-medium'>{sourcesBase}</div>
  }

  const handleClickIntegration = (integrationType: IntegrationServiceId) => {
    const isSelected = sources.some((s) => s.type === 'integration' && s.integrationType === integrationType)
    if (isSelected) {
      onChange(sources.filter((s) => !(s.type === 'integration' && s.integrationType === integrationType)))
    } else {
      const integrationSources = sources.filter((s) => s.type === 'integration')
      if (integrationSources.length >= _MAX_INTEGRATION_SOURCES) {
        const firstIntegrationIndex = sources.findIndex((s) => s.type === 'integration')
        const filteredSources = sources.filter((_, index) => index !== firstIntegrationIndex)
        onChange([...filteredSources, { type: 'integration', integrationType }])
      } else {
        onChange([...sources, { type: 'integration', integrationType }])
      }
    }
  }

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='paragraph-medium'>{sourcesBase} For anything I can't find there, I can pull in other sources. Pick what other sources should I use.</div>
      <div className='d-flex gap-3 flex-wrap'>
        {Object.values(supportedUserIntegrationsByCategory).map((serviceIds) => {
          return serviceIds.map((serviceId) => {
            const isSelected = sources.some((s) => s.type === 'integration' && s.integrationType === serviceId)

            return (
              <Button
                key={serviceId}
                variant='header'
                className='d-flex gap-2 align-items-center zocks-px-0.75 py-2 zocks-border-0.5'
                onClick={() => handleClickIntegration(serviceId)}
              >
                <IntegrationIcon serviceId={serviceId} className='cross-client-source-icon' />
                <div className='d-flex zocks-gap-0.75 align-items-center'>
                  <div className='paragraph-small bold'>{getIntegrationDisplayName(serviceId)}</div>
                  <Form.Check
                    type='checkbox'
                    checked={isSelected}
                    onChange={() => handleClickIntegration(serviceId)}
                    className='cross-client-source-check cursor-pointer'
                  />
                </div>
              </Button>
            )
          })
        })}
      </div>
      <div className='d-flex gap-2 align-items-center'>
        <i className='bi bi-info-square' />
        <div className='paragraph-small'>Additional sources may increase processing time</div>
      </div>
    </div>
  )
}
